import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import Select from "react-select";
import CountUp from "react-countup";
import { CorporationStore } from "../corporation_master/CorporationStore";
import { DashBoardStore } from "./DashBoardStore";
import { getMasterAPIStore } from "../../../utils/getMasterAPIStore";
import DashboardCardLoader from "../../Common/DashboardCardLoader/DashboardCardLoader";
import KMLmap from "./googlemap/KMLmap";
import combinedMap from "../../../assets/images/GHMC-MMC-CMC_combined.png";
import ghmcMap from "../../../assets/images/GMC.png";
import mmcMap from "../../../assets/images/MMC_Malkajgiri.png";
import cmcMap from "../../../assets/images/CMC.png";

const mapRegionOptions = [
  { value: "all", label: "All", image: combinedMap },
  { value: "ghmc", label: "GHMC", image: ghmcMap },
  { value: "mmc", label: "MMC", image: mmcMap },
  { value: "cmc", label: "CMC", image: cmcMap },
  /*{ value: "ghmc-mmc", label: "GHMC + MMC", image: combinedMap },
  { value: "ghmc-cmc", label: "GHMC + CMC", image: combinedMap },
  { value: "mmc-cmc", label: "MMC + CMC", image: combinedMap },*/
];

const fmtDT = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const getSATFromDate = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return fmtDT(d);
};
const getSATToDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(23, 59, 0, 0);
  return fmtDT(d);
};
const getShiftFromDate = () => {
  const d = new Date();
  d.setHours(3, 30, 0, 0);
  return fmtDT(d);
};
const getShiftToDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(3, 30, 0, 0);
  return fmtDT(d);
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "0px",
    backgroundColor: "#ececec",
    border: "1px solid #dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,0.25)" : "none",
    borderColor: state.isFocused ? "#80bdff" : "#ced4da",
    minHeight: "38px",
    fontSize: "1rem",
    "&:hover": { borderColor: "#ced4da" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0px 8px" }),
  placeholder: (base) => ({
    ...base,
    color: "#6c757d",
    fontSize: "1rem",
    marginLeft: "6px",
  }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "#fff" }),
  dropdownIndicator: (base) => ({ ...base, padding: "6px", color: "#6c757d" }),
};

const SectionHeading = ({ icon, title, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      margin: "16px 0 12px",
      padding: "10px 16px",
      background: `linear-gradient(90deg, ${color}18 0%, transparent 100%)`,
      borderLeft: `4px solid ${color}`,
      borderRadius: "6px 16px 16px 6px",
    }}
  >
    <span
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "8px",
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <i
        className={`fa-solid ${icon}`}
        style={{ color: "#fff", fontSize: "14px" }}
      />
    </span>
    <h5
      style={{
        margin: 0,
        fontWeight: 700,
        color: "#2c3e50",
        fontSize: "15px",
        letterSpacing: "0.3px",
      }}
    >
      {title}
    </h5>
  </div>
);

const Cards = ({ cards, loading }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  if (loading) return <DashboardCardLoader count={3} />;
  return (
    <div className="row mt-3 g-3">
      {cards.map((card, index) => {
        const isHovered = hoveredCard === index;
        return (
          <div className="col-md-4" key={index}>
            <div
              className="card border-0"
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                borderRadius: "12px",
                background: "#f8f9fa",
                boxShadow: isHovered
                  ? "0 12px 28px rgba(0,0,0,0.15)"
                  : "0 2px 8px rgba(0,0,0,0.08)",
                transform: isHovered
                  ? "translateY(-6px) scale(1.02)"
                  : "translateY(0) scale(1)",
                transition: "all 0.25s ease",
                cursor: "pointer",
              }}
            >
              <div className="card-body" style={{ padding: "20px 24px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "4px",
                    backgroundColor: card.accent,
                    borderRadius: "2px",
                    marginBottom: "14px",
                  }}
                />
                <p
                  className="mb-1"
                  style={{
                    fontSize: "13px",
                    color: "#6c757d",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {card.title}
                </p>
                <h3
                  className="mb-0"
                  style={{ color: card.accent, fontWeight: 700 }}
                >
                  <CountUp
                    end={card.value}
                    duration={3}
                    decimals={card.title === "Percentage %" ? 2 : 0}
                  />
                </h3>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getRegionFromUserCorporations = (userCorporations) => {
  if (!Array.isArray(userCorporations) || userCorporations.length === 0) {
    return "all";
  }

  const normalized = userCorporations
    .map((corp) => (corp.corporationName || corp.name || "").toString().trim().toLowerCase())
    .filter(Boolean);

  const regions = new Set();
  normalized.forEach((name) => {
    if (name.includes("ghmc") || name.includes("gmc")) regions.add("ghmc");
    else if (name.includes("mmc")) regions.add("mmc");
    else if (name.includes("cmc")) regions.add("cmc");
  });

  if (regions.size === 1) {
    return regions.values().next().value;
  }

  if (regions.size === 2) {
    const hasGhmc = regions.has("ghmc");
    const hasMmc = regions.has("mmc");
    const hasCmc = regions.has("cmc");

    if (hasGhmc && hasMmc) return "ghmc-mmc";
    if (hasGhmc && hasCmc) return "ghmc-cmc";
    if (hasMmc && hasCmc) return "mmc-cmc";
  }

  return "all";
};

const CombinedDashboard = () => {
  const { fetchUserCorporations, UserCorporations } = CorporationStore();
  const {
    fetchSATDashboardDetails,
    SATDashboardDetails,
    isSATDashboardLoading,
    fetchScvDashboardDetails,
    ScvDashboardDetails,
    isScvDashboardLoading,
    fetchGvpDashboardDetails,
    GvpDashboardDetails,
    isGvpDashboardLoading,
  } = DashBoardStore();
  const { shiftLoading, shiftData } = getMasterAPIStore();

  const [selectedRegion, setSelectedRegion] = useState("all");
  const [hasRegionInitialized, setHasRegionInitialized] = useState(false);

  const visibleRegionOptions = useMemo(() => {
    if (!UserCorporations || UserCorporations.length === 0) return mapRegionOptions;

    const normalized = UserCorporations
      .map((c) => (c.corporationName || c.name || "").toString().trim().toLowerCase())
      .filter(Boolean);

    const regions = new Set();
    normalized.forEach((name) => {
      if (name.includes("ghmc") || name.includes("gmc")) regions.add("ghmc");
      else if (name.includes("mmc")) regions.add("mmc");
      else if (name.includes("cmc")) regions.add("cmc");
    });

    const individual = mapRegionOptions.filter(
      (o) => o.value !== "all" && regions.has(o.value)
    );
    return individual.length > 1
      ? [mapRegionOptions[0], ...individual]
      : individual;
  }, [UserCorporations]);

  const activeRegion =
    visibleRegionOptions.find((o) => o.value === selectedRegion) ||
    visibleRegionOptions[0] ||
    mapRegionOptions[0];

  useEffect(() => {
    if (!hasRegionInitialized && UserCorporations.length > 0) {
      setSelectedRegion(getRegionFromUserCorporations(UserCorporations));
      setHasRegionInitialized(true);
    }
  }, [UserCorporations, hasRegionInitialized]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUserCorporations();
    fetchSATDashboardDetails({
      startDate: getSATFromDate(),
      endDate: getSATToDate(),
      corporationIds: [],
    });
    fetchScvDashboardDetails({
      startDate: getShiftFromDate(),
      endDate: getShiftToDate(),
      corporationIds: [],
    });
    fetchGvpDashboardDetails({
      startDate: getShiftFromDate(),
      endDate: getShiftToDate(),
      shiftId: "",
      corporationIds: [],
    });
  }, []);

  const corporationOptions = UserCorporations.map((corp) => ({
    value: corp.corporationId,
    label: corp.corporationName,
  }));

  const satCards = [
    {
      title: "SAT Masters",
      value: SATDashboardDetails?.[0]?.activeVehicle ?? 0,
      accent: "#4A90D9",
    },
    {
      title: "SAT Deployment",
      value: SATDashboardDetails?.[0]?.reportedAtWard ?? 0,
      accent: "#27AE60",
    },
    {
      title: "SAT Reported at TS/SCTP",
      value: SATDashboardDetails?.[0]?.reportedAtTS ?? 0,
      accent: "#E67E22",
    },
  ];

  const scvCards = [
    {
      title: "SCV Masters",
      value: ScvDashboardDetails?.[0]?.activeScv ?? 0,
      accent: "#4A90D9",
    },
    {
      title: "SCV Deployed",
      value: ScvDashboardDetails?.[0]?.deployedScv ?? 0,
      accent: "#27AE60",
    },
    {
      title: "SCV Reported at TS/SCTP",
      value: ScvDashboardDetails?.[0]?.scvReportedAtTSSCTP ?? 0,
      accent: "#E67E22",
    },
  ];

  const gvpCards = [
    {
      title: "GVP Master",
      value: GvpDashboardDetails?.[0]?.TotalGvps ?? 0,
      accent: "#4A90D9",
    },
    {
      title: "GVPs Cleared",
      value: GvpDashboardDetails?.[0]?.UniqueGVPCleared ?? 0,
      accent: "#27AE60",
    },
    {
      title: "Percentage %",
      value: GvpDashboardDetails?.[0]?.Percentage ?? 0,
      accent: "#E67E22",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <style>
        {`@keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }`}
      </style>
      <div className="content">
        <div className="page-content">
          {/* SATs Dashboard */}
          <SectionHeading
            icon="fa-truck"
            title="SATs Dashboard"
            color="#4A90D9"
          />
          <div className="card mb-3">
            <div className="card-body">
              <Formik
                initialValues={{
                  startDate: getSATFromDate(),
                  endDate: getSATToDate(),
                  corporationIds: [],
                }}
                onSubmit={(v) => fetchSATDashboardDetails(v)}
                enableReinitialize
              >
                {({ values, setFieldValue }) => (
                  <Form>
                    <div className="row">
                      {/* <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>From Date & Time</label>
                          <Field
                            type="datetime-local"
                            className="form-control"
                            name="startDate"
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          />
                        </div>
                      </div> */}
                      {/* <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>To Date & Time</label>
                          <Field
                            type="datetime-local"
                            className="form-control"
                            name="endDate"
                            min={values.startDate}
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          />
                        </div>
                      </div> */}
                      <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>Corporation</label>
                          <Select
                            isMulti
                            options={corporationOptions}
                            value={corporationOptions.filter((o) =>
                              values.corporationIds.includes(o.value),
                            )}
                            onChange={(sel) =>
                              setFieldValue(
                                "corporationIds",
                                (sel || []).map((c) => c.value),
                              )
                            }
                            placeholder="Select Corporation(s)"
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 text-start mt-4">
                        <button type="submit" className="btn search-swm">
                          Search
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
              <Cards cards={satCards} loading={isSATDashboardLoading} />
            </div>
          </div>

          {/* SCVs Dashboard */}
          <SectionHeading
            icon="fa-van-shuttle"
            title="SCVs Dashboard"
            color="#27AE60"
          />
          <div className="card mb-3">
            <div className="card-body">
              <Formik
                initialValues={{
                  startDate: getShiftFromDate(),
                  endDate: getShiftToDate(),
                  corporationIds: [],
                }}
                onSubmit={(v) => fetchScvDashboardDetails(v)}
                enableReinitialize
              >
                {({ values, setFieldValue }) => (
                  <Form>
                    <div className="row">
                      {/* <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>From Date & Time</label>
                          <Field
                            type="datetime-local"
                            className="form-control"
                            name="startDate"
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          />
                        </div>
                      </div> */}
                      {/* <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>To Date & Time</label>
                          <Field
                            type="datetime-local"
                            className="form-control"
                            name="endDate"
                            min={values.startDate}
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          />
                        </div>
                      </div> */}
                      <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>Corporation</label>
                          <Select
                            isMulti
                            options={corporationOptions}
                            value={corporationOptions.filter((o) =>
                              values.corporationIds.includes(o.value),
                            )}
                            onChange={(sel) =>
                              setFieldValue(
                                "corporationIds",
                                (sel || []).map((c) => c.value),
                              )
                            }
                            placeholder="Select Corporation(s)"
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 text-start mt-4">
                        <button type="submit" className="btn search-swm">
                          Search
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
              <Cards cards={scvCards} loading={isScvDashboardLoading} />
            </div>
          </div>

          {/* GVPs Dashboard */}
          <SectionHeading
            icon="fa-trash-can"
            title="GVPs Dashboard"
            color="#E67E22"
          />
          <div className="card mb-3">
            <div className="card-body">
              <Formik
                initialValues={{
                  startDate: getShiftFromDate(),
                  endDate: getShiftToDate(),
                  shiftId: "",
                  corporationIds: [],
                }}
                onSubmit={(v) => fetchGvpDashboardDetails(v)}
                enableReinitialize
              >
                {({ values, setFieldValue }) => (
                  <Form>
                    <div className="row">
                      {/* <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>From Date & Time</label>
                          <Field
                            type="datetime-local"
                            className="form-control"
                            name="startDate"
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          />
                        </div>
                      </div> */}
                      {/* <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>To Date & Time</label>
                          <Field
                            type="datetime-local"
                            className="form-control"
                            name="endDate"
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          />
                        </div>
                      </div> */}
                      <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>Corporation</label>
                          <Select
                            isMulti
                            options={corporationOptions}
                            value={corporationOptions.filter((o) =>
                              values.corporationIds.includes(o.value),
                            )}
                            onChange={(sel) =>
                              setFieldValue(
                                "corporationIds",
                                (sel || []).map((c) => c.value),
                              )
                            }
                            placeholder="Select Corporation(s)"
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 text-start">
                        <div className="form-group">
                          <label>Select Shift</label>
                          <Field
                            as="select"
                            name="shiftId"
                            className="form-select"
                            style={{
                              backgroundColor: "#FFF",
                              borderRadius: "0px",
                            }}
                          >
                            {shiftLoading && (
                              <option value="">Loading...</option>
                            )}
                            <option value="">All</option>
                            {Array.isArray(shiftData) &&
                              shiftData.map((s) => (
                                <option key={s.shiftId} value={s.shiftId}>
                                  {s.shiftName}
                                </option>
                              ))}
                          </Field>
                        </div>

                      </div>
                      <div className="col-md-3 text-start mt-3">
                        <button type="submit" className="btn search-swm">
                          Search
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
              <Cards cards={gvpCards} loading={isGvpDashboardLoading} />
            </div>
          </div>

          {/* Map - Client does not want image in dashboard */}
          {/* <SectionHeading
            icon="fa-map-location-dot"
            title="Corporation Map"
            color="#8E44AD"
          />
          <div className="card mb-3">
            <div className="card-body">
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#2c3e50",
                    }}
                  >
                    Select Region
                  </span>
                  <div style={{ minWidth: "220px" }}>
                    <Select
                      options={visibleRegionOptions}
                      value={activeRegion}
                      onChange={(sel) => setSelectedRegion(sel.value)}
                      isSearchable={false}
                      styles={selectStyles}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {visibleRegionOptions.map((o) => {
                    const isActive = o.value === selectedRegion;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setSelectedRegion(o.value)}
                        style={{
                          border: isActive
                            ? "1px solid #8E44AD"
                            : "1px solid #dee2e6",
                          background: isActive ? "#8E44AD" : "#fff",
                          color: isActive ? "#fff" : "#6c757d",
                          padding: "6px 16px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  position: "relative",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #eef1f5 100%)",
                  border: "1px solid #e9ecef",
                  padding: "16px",
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "16px",
                    left: "16px",
                    background: "#8E44AD",
                    color: "#fff",
                    padding: "4px 14px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    boxShadow: "0 2px 8px rgba(142,68,173,0.3)",
                  }}
                >
                  {activeRegion.label}
                </span>
                <img
                  key={activeRegion.value}
                  src={activeRegion.image}
                  alt={`${activeRegion.label} map`}
                  style={{
                    width: "100%",
                    maxHeight: "720px",
                    objectFit: "contain",
                    animation: "fadeIn 0.4s ease",
                  }}
                />
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default CombinedDashboard;
